<template>
  <div class="otp-container">
    <h3>Enter Authentication Code</h3>
    <p>Please enter the 5-digit code to complete your deposit</p>
    
    <div class="otp-inputs">
      <input
        v-for="(digit, index) in digits"
        :key="index"
        :ref="`input-${index}`"
        v-model="digits[index]"
        type="text"
        maxlength="1"
        class="otp-input"
        :disabled="disabled"
        @input="handleInput(index, $event)"
        @keydown="handleKeydown(index, $event)"
        @paste="handlePaste($event)"
      />
    </div>
    
    <div v-if="showError" class="error-message">
      Please enter a valid 4-digit code
    </div>
    
    <div class="otp-actions">
      <button 
        class="btn-verify" 
        @click="verifyCode" 
        :disabled="disabled || !isCodeComplete"
      >
        <span v-if="isLoading">Verifying...</span>
        <span v-else>Verify Code</span>
      </button>
      
      <button 
        class="btn-cancel" 
        @click="cancelVerification"
        :disabled="disabled"
      >
        Cancel
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OtpInput',
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['verify-code', 'cancel-verification'],
  data() {
    return {
      digits: ['', '', '', '', ''],
      showError: false
    };
  },
  computed: {
    isCodeComplete() {
      return this.digits.every(digit => digit !== '' && /^\d$/.test(digit));
    },
    otpCode() {
      return this.digits.join('');
    }
  },
  methods: {
    handleInput(index, event) {
      const value = event.target.value;
      
      // Solo permitir números
      if (value && !/^\d$/.test(value)) {
        event.target.value = '';
        this.digits[index] = '';
        return;
      }
      
      this.digits[index] = value;
      this.showError = false;
      
      // Auto-focus al siguiente input
      if (value && index < 4) {
        this.focusInput(index + 1);
      }
    },
    
    handleKeydown(index, event) {
      // Manejar backspace
      if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
        this.focusInput(index - 1);
      }
      
      // Manejar Enter para verificar
      if (event.key === 'Enter' && this.isCodeComplete) {
        this.verifyCode();
      }
    },
    
    handlePaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData.getData('text');

      // Validar que sean 5 dígitos
      if (/^\d{5}$/.test(pastedData)) {
        const pastedDigits = pastedData.split('');
        for (let i = 0; i < 5; i++) {
          this.digits[i] = pastedDigits[i];
        }
        this.focusInput(4); // Focus en el último input
      }
    },
    
    focusInput(index) {
      this.$nextTick(() => {
        const input = this.$refs[`input-${index}`];
        if (input && input[0]) {
          input[0].focus();
        }
      });
    },
    
    verifyCode() {
      if (!this.isCodeComplete) {
        this.showError = true;
        return;
      }
      
      this.$emit('verify-code', this.otpCode);
    },
    
    cancelVerification() {
      this.$emit('cancel-verification');
    },
    
    clearCode() {
      this.digits = ['', '', '', '', ''];
      this.showError = false;
      this.focusInput(0);
    }
  },
  
  mounted() {
    // Auto-focus en el primer input
    this.focusInput(0);
  }
};
</script>

<style scoped>
.otp-container {
  background-color: #e7e7e7;
  padding: 30px;
  text-align: center;
  border-radius: 8px;
  max-width: 400px;
  margin: 0 auto;
}

.otp-container h3 {
  color: #333;
  margin-bottom: 10px;
  font-size: 1.4rem;
}

.otp-container p {
  color: #666;
  margin-bottom: 25px;
}

.otp-inputs {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 20px;
}

.otp-input {
  width: 50px;
  height: 50px;
  font-size: 24px;
  font-weight: bold;
  text-align: center;
  border: 2px solid #ccc;
  border-radius: 8px;
  background-color: #fff;
  transition: border-color 0.3s ease;
}

.otp-input:focus {
  outline: none;
  border-color: #ff8800;
  box-shadow: 0 0 5px rgba(255, 136, 0, 0.3);
}

.otp-input:disabled {
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.error-message {
  color: #cc371b;
  font-size: 14px;
  margin-bottom: 20px;
}

.otp-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
}

.btn-verify,
.btn-cancel {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.btn-verify {
  background-color: #cddc39;
  color: #000;
  min-width: 120px;
}

.btn-verify:hover:not(:disabled) {
  background-color: #b7dc39;
}

.btn-verify:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.btn-cancel {
  background-color: #f99f44;
  color: #000;
}

.btn-cancel:hover:not(:disabled) {
  background-color: #f67d3c;
}

.btn-cancel:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>
